import { CompanyRoles } from '@domain/company';
import { type PostHog } from 'posthog-js';

export const COMPANY_GROUP_TYPE = 'company';
export const PERSON_FINGERPRINT_STORAGE_KEY = 'ph_company_person_fingerprint';
// v2: group properties gained `id` (flag release conditions match on group
// properties, not the group key) — bump invalidates already-sent fingerprints.
export const GROUP_FINGERPRINT_STORAGE_KEY = 'ph_company_group_fingerprint_v2';

const readFingerprint = (key: string): string | null => {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeFingerprint = (key: string, fingerprint: string): void => {
  try {
    globalThis.localStorage.setItem(key, fingerprint);
  } catch {
    // Storage unavailable: the event was already sent, worst case it is resent next load.
  }
};

/**
 * Associates the session with the company group and stamps company_id/company_role
 * on every captured event. group() and register() are local-only (no events captured),
 * so re-running them on every auth-context refetch is free and keeps state correct
 * after posthog.reset().
 */
export const identifyCompanyContext = (
  posthog: PostHog,
  { userId, companyId, role }: { userId: string; companyId: number; role: CompanyRoles }
): void => {
  posthog.group(COMPANY_GROUP_TYPE, companyId.toString());
  posthog.register({ company_id: companyId, company_role: role });

  // The opt-out gates the FINGERPRINT, not the call. `setPersonProperties()` does two things:
  // `setPersonPropertiesForFlags()`, which ships the properties as `person_properties` on the next
  // `/flags` request, and then a `$set` capture. The capture needs no guard of ours — `capture()`
  // opens with `if (this.is_capturing())` — so skipping the call would only keep company_id and
  // company_role out of flag evaluation, which is the opposite of what an impersonated session is
  // for. What must not happen is writing the fingerprint: the impersonated user's own next session
  // would read it as "already sent" and never set its real person properties.
  const isOptedOut = posthog.has_opted_out_capturing();

  // Skipped when nothing changed, so the $set event fires on a real change rather than per page
  // load or navigation. While opted out there is no event to spare, but re-sending the same
  // properties to /flags costs nothing either, so the same skip applies.
  const fingerprint = `${userId}:${companyId}:${role}`;
  if (!isOptedOut && readFingerprint(PERSON_FINGERPRINT_STORAGE_KEY) === fingerprint) return;
  posthog.setPersonProperties({ company_id: companyId, company_role: role });
  if (!isOptedOut) writeFingerprint(PERSON_FINGERPRINT_STORAGE_KEY, fingerprint);
};

/**
 * Sets the company group display properties. group() with properties captures a
 * billable $groupidentify event, so it only fires when the display data changes.
 */
export const identifyCompanyGroupProperties = (
  posthog: PostHog,
  { companyId, name, commercialName }: { companyId: number; name: string; commercialName: string }
): void => {
  // Associates the group on every call, so it survives a posthog.reset() even when the fingerprint
  // below short-circuits. Local-only: group() without properties captures nothing.
  posthog.group(COMPANY_GROUP_TYPE, companyId.toString());

  // Same reasoning as identifyCompanyContext. `group(type, key, props)` calls
  // `setGroupPropertiesForFlags()` outside its `$groupidentify` capture, so skipping the call while
  // opted out would drop exactly the group properties a company-scoped flag resolves against — and
  // resolving as the impersonated company is the point of the impersonated session.
  const isOptedOut = posthog.has_opted_out_capturing();

  const fingerprint = `${companyId}:${name}:${commercialName}`;
  if (!isOptedOut && readFingerprint(GROUP_FINGERPRINT_STORAGE_KEY) === fingerprint) return;
  posthog.group(COMPANY_GROUP_TYPE, companyId.toString(), { id: companyId, name, commercial_name: commercialName });
  if (!isOptedOut) writeFingerprint(GROUP_FINGERPRINT_STORAGE_KEY, fingerprint);
};

/**
 * Detaches subsequent events from any company (select-venue, user settings).
 * Local-only, captures no events. Logout is covered separately by posthog.reset().
 */
export const clearCompanyContext = (posthog: PostHog): void => {
  posthog.resetGroups();
  posthog.unregister('company_id');
  posthog.unregister('company_role');
};
